import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { deletePass, listPasses } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n, type Lang, type Translate } from '../i18n';
import { describeApiError } from '../lib/apiErrors';
import type { DemoPass, PassTemplateKind } from '../types';

function formatDate(iso: string, lang: Lang): string {
  return new Date(iso).toLocaleDateString(lang === 'hu' ? 'hu-HU' : 'en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

/**
 * Per-template visual label + colour. The two demo templates follow the
 * studios' own wording ("Utazási biztosítás" / "Általános biztosítás") — no
 * pass-style names ("Repjegy") in the copy; the legacy template kinds keep
 * their labels for old passes.
 */
function templateBadge(kind: PassTemplateKind, t: Translate): { label: string; classes: string } {
  switch (kind) {
    case 'travel':
      return { label: t('badge.travel'), classes: 'bg-sky-100 text-sky-800' };
    case 'motor_kotelezo':
      return { label: t('badge.motorKotelezo'), classes: 'bg-emerald-100 text-emerald-800' };
    case 'motor_casco':
      return { label: t('badge.motorCasco'), classes: 'bg-teal-100 text-teal-800' };
    case 'insurance':
      return { label: t('badge.insurance'), classes: 'bg-violet-100 text-violet-800' };
    case 'assistance':
      return { label: t('badge.assistance'), classes: 'bg-amber-100 text-amber-800' };
    case 'life_accident':
      return { label: t('badge.lifeAccident'), classes: 'bg-rose-100 text-rose-800' };
    default:
      return { label: String(kind).toUpperCase(), classes: 'bg-slate-100 text-slate-700' };
  }
}

/**
 * Compact "what's this pass about" cell. Travel passes show
 * route/date; non-travel passes show the most identifying field
 * from servicePayload (policy / plate / lounge / beneficiary).
 */
function passSummary(p: DemoPass): string {
  if (p.templateKind === 'travel') {
    if (p.flightIata && p.originIata && p.destinationIata) {
      const date = p.flightDate ? ` · ${p.flightDate}` : '';
      return `${p.flightIata} · ${p.originIata} → ${p.destinationIata}${date}`;
    }
    return p.flightIata ?? '—';
  }
  const sp = (p.servicePayload ?? {}) as Record<string, unknown>;
  const pick = (k: string): string | null => {
    const v = sp[k];
    return typeof v === 'string' && v.trim() ? v : null;
  };
  return (
    pick('licensePlate') ??
    pick('policyNumber') ??
    pick('loungeName') ??
    pick('productName') ??
    pick('beneficiaryName') ??
    '—'
  );
}

function statusPill(pass: DemoPass, t: Translate): { label: string; classes: string } {
  if (pass.status === 'expired') {
    return { label: t('status.expired'), classes: 'bg-slate-100 text-slate-600' };
  }
  // Only a FLIGHT-BOUND travel pass has a flight subscription — the studio
  // flight is optional, so a flightless travel pass (and every non-travel
  // pass) must never advertise a live flight state ('IDŐBEN', 'KÉSIK'…).
  if (pass.templateKind !== 'travel' || !pass.flightIata) {
    return { label: t('status.active'), classes: 'bg-brand-100 text-brand-800' };
  }
  const live = pass.liveStatus;
  if (!live || live.state === 'unknown') {
    return { label: t('status.active'), classes: 'bg-brand-100 text-brand-800' };
  }
  switch (live.state) {
    case 'on_time':
      return { label: t('status.onTime'), classes: 'bg-emerald-100 text-emerald-800' };
    case 'delayed':
      return {
        label: live.delayMinutes
          ? t('status.delayedMinutes', { n: live.delayMinutes })
          : t('status.delayed'),
        classes: 'bg-amber-100 text-amber-800',
      };
    case 'cancelled':
      return { label: t('status.cancelled'), classes: 'bg-red-100 text-red-800' };
    case 'boarding':
      return { label: t('status.boarding'), classes: 'bg-blue-100 text-blue-800' };
    case 'departed':
      return { label: t('status.departed'), classes: 'bg-indigo-100 text-indigo-800' };
    case 'arrived':
      return { label: t('status.arrived'), classes: 'bg-slate-100 text-slate-700' };
    case 'diverted':
      return { label: t('status.diverted'), classes: 'bg-orange-100 text-orange-800' };
    default:
      return { label: t('status.active'), classes: 'bg-brand-100 text-brand-800' };
  }
}

/** Which wallet(s) the pass was minted for — read off the save links. */
function platformChips(p: DemoPass): string[] {
  const chips: string[] = [];
  if (p.appleSaveUrl) chips.push('Apple');
  if (p.googleSaveUrl) chips.push('Google');
  return chips;
}

const CHIP = 'text-[10px] font-bold rounded-full px-2 py-0.5 tracking-wider';
/** One grid for the md+ header row and every list row, so the columns line up. */
const ROW_GRID = 'md:grid md:grid-cols-[2fr_1fr_2fr_1fr_1fr_auto] md:items-center md:gap-4';

export function DashboardPage(): JSX.Element {
  const { partner } = useAuth();
  const { lang, t } = useI18n();
  const queryClient = useQueryClient();
  const {
    data: passes,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['passes'],
    queryFn: () => listPasses(),
    refetchInterval: 30_000,
  });

  // Inline delete: ask once, then mutate. We track which row's confirm
  // is pending so the button can flip to "Megerősít" without a modal.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePass(id),
    onSuccess: () => {
      setConfirmingId(null);
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
    },
  });

  const greeting =
    partner?.displayName ?? partner?.email?.split('@')[0] ?? t('dashboard.greetingFallback');

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {t('dashboard.greeting', { name: greeting })}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t('dashboard.intro')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/studio/apple" className="btn-primary">
            {t('dashboard.newApplePass')}
          </Link>
          <Link to="/studio/google" className="btn-secondary">
            {t('dashboard.newGooglePass')}
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="card p-12 text-center text-slate-500">{t('common.loading')}</div>
      ) : isError && !passes ? (
        // A failed load is not an empty account — say so and offer a retry
        // (a 401 never gets here: the session-expiry hook signs out first).
        <div className="card p-8 text-center space-y-3">
          <p className="text-sm text-red-700">{describeApiError(error, t)}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="btn-secondary"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : passes && passes.length > 0 ? (
        <div className="card overflow-hidden">
          {/* Column headings — from md up, where the rows are a table-like grid. */}
          <div
            className={`hidden ${ROW_GRID} bg-slate-50 px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium`}
            aria-hidden="true"
          >
            <span>{t('dashboard.th.recipient')}</span>
            <span>{t('dashboard.th.template')}</span>
            <span>{t('dashboard.th.details')}</span>
            <span>{t('dashboard.th.status')}</span>
            <span>{t('dashboard.th.expires')}</span>
            <span className="text-right">{t('dashboard.th.actions')}</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {passes.map((p) => {
              const pill = statusPill(p, t);
              const tpl = templateBadge(p.templateKind, t);
              const isConfirming = confirmingId === p.id;
              const isDeleting = deleteMutation.isPending && deleteMutation.variables === p.id;
              return (
                // Below md every pass is a stacked card; from md up the same
                // markup lays out as the table-like grid under the headings.
                <li key={p.id} className={`${ROW_GRID} px-5 py-4 md:py-3 hover:bg-slate-50`}>
                  <div className="min-w-0">
                    <Link
                      to={`/passes/${p.id}`}
                      className="font-medium text-slate-900 hover:text-brand-700"
                    >
                      {p.recipientName}
                    </Link>
                    <div className="text-xs text-slate-500 truncate">{p.recipientEmail}</div>
                  </div>
                  <div className="mt-2 md:mt-0 flex flex-wrap items-center gap-1.5">
                    <span className={`${CHIP} ${tpl.classes}`}>{tpl.label}</span>
                    {platformChips(p).map((chip) => (
                      <span key={chip} className={`${CHIP} bg-slate-100 text-slate-700`}>
                        {chip}
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 md:mt-0 font-mono text-xs text-slate-700 break-words">
                    {passSummary(p)}
                  </div>
                  <div className="mt-2 md:mt-0">
                    <span className={`${CHIP} ${pill.classes}`}>{pill.label}</span>
                  </div>
                  <div className="mt-1 md:mt-0 text-xs md:text-sm text-slate-500">
                    <span className="md:hidden">{t('dashboard.th.expires')}: </span>
                    {formatDate(p.expiresAt, lang)}
                  </div>
                  <div className="mt-3 md:mt-0 flex items-center gap-3 md:justify-end">
                    <Link
                      to={`/passes/${p.id}`}
                      className="text-xs font-medium text-brand-700 hover:underline"
                    >
                      {t('dashboard.open')}
                    </Link>
                    {isConfirming ? (
                      <>
                        <button
                          type="button"
                          onClick={() => deleteMutation.mutate(p.id)}
                          disabled={isDeleting}
                          className="text-xs font-medium text-red-700 hover:text-red-900 disabled:opacity-50"
                        >
                          {isDeleting ? t('common.deleting') : t('dashboard.confirmDelete')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmingId(null)}
                          disabled={isDeleting}
                          className="text-xs text-slate-500 hover:text-slate-700"
                        >
                          {t('common.cancel')}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmingId(p.id)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        {t('common.delete')}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {deleteMutation.error && (
            <div className="px-5 py-3 bg-red-50 ring-1 ring-red-200 text-sm text-red-800">
              {describeApiError(deleteMutation.error, t)}
            </div>
          )}
        </div>
      ) : (
        <div className="card p-8 sm:p-12 text-center">
          <h2 className="font-semibold text-slate-900">{t('dashboard.emptyTitle')}</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{t('dashboard.emptyBody')}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Link to="/studio/apple" className="btn-primary">
              {t('dashboard.newApplePass')}
            </Link>
            <Link to="/studio/google" className="btn-secondary">
              {t('dashboard.newGooglePass')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
